import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixLoyaltyReferralTrackerV1ReferralEventsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/referral-tracker',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/referral-tracker',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/referral-tracker',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/referral-tracker',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/referral-tracker',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/referral-tracker',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/referral-tracker',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/referral-tracker',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/referral-tracker',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_referral_tracker';

/** Retrieves a referral event by ID. */
export function getReferralEvent(payload: object): RequestOptionsFactory<any> {
  function __getReferralEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.referral_event',
      method: 'GET' as any,
      methodFqn:
        'wix.loyalty.referral.tracker.v1.ReferralEvents.GetReferralEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralTrackerV1ReferralEventsUrl({
        protoPath: '/v1/referral-events/{referralEventId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'referralEvent.createdDate' },
              { path: 'referralEvent.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getReferralEvent;
}

/**
 * Creates a query to retrieve a list of referral events.
 *
 * The `queryReferralEvents()` function builds a query to retrieve a list of referral events and returns a `ReferralEventsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `ReferralEventsQueryBuilder` functions onto the query. `ReferralEventsQueryBuilder` functions enable you to sort, filter, and control the results `queryReferralEvents()` returns.
 *
 * `queryReferralEvents()` runs with these `ReferralEventsQueryBuilder` defaults, which you can override:
 *
 * - skip(0)
 * - limit(50)
 * - descending("_createdDate")
 *
 * The functions that are chained to `queryReferralEvents()` are applied in the order they're called. For example, if you apply ascending('actionEvent') and then descending('rewardEvent'), the results are sorted first by the action events, and then, if there are multiple results with the same action events, the items are sorted by reward events.
 */
export function queryReferralEvent(
  payload: object
): RequestOptionsFactory<any> {
  function __queryReferralEvent({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.referral_event',
      method: 'POST' as any,
      methodFqn:
        'wix.loyalty.referral.tracker.v1.ReferralEvents.QueryReferralEvent',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralTrackerV1ReferralEventsUrl({
        protoPath: '/v1/referral-events/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'referralEvents.createdDate' },
              { path: 'referralEvents.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryReferralEvent;
}

/** Retrieves referral statistics. */
export function getReferralStatistics(
  payload: object
): RequestOptionsFactory<any> {
  function __getReferralStatistics({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.referral_event',
      method: 'GET' as any,
      methodFqn:
        'wix.loyalty.referral.tracker.v1.ReferralEvents.GetReferralStatistics',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralTrackerV1ReferralEventsUrl({
        protoPath: '/v1/referral-statistics',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getReferralStatistics;
}

/**
 * Retrieves a list of referring customer totals, given the provided paging, filtering, and sorting.
 *
 * To learn about working with Query endpoints, see
 * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
 * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/pagination),
 * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
 */
export function queryReferringCustomerTotals(
  payload: object
): RequestOptionsFactory<any> {
  function __queryReferringCustomerTotals({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.referral_event',
      method: 'POST' as any,
      methodFqn:
        'wix.loyalty.referral.tracker.v1.ReferralEvents.QueryReferringCustomerTotals',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralTrackerV1ReferralEventsUrl({
        protoPath: '/v1/referring-customer-totals/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'referringCustomerTotals.lastSuccessfulReferral' },
              { path: 'referringCustomerTotals.lastFriendAction' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryReferringCustomerTotals;
}

/**
 * Retrieves a list of referred friend actions, given the provided paging, filtering, and sorting.
 *
 * To learn about working with Query endpoints, see
 * [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language),
 * [Sorting and Paging](https://dev.wix.com/api/rest/getting-started/pagination),
 * and [Field Projection](https://dev.wix.com/api/rest/getting-started/field-projection).
 */
export function queryReferredFriendActions(
  payload: object
): RequestOptionsFactory<any> {
  function __queryReferredFriendActions({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.loyalty.referral.v1.referral_event',
      method: 'POST' as any,
      methodFqn:
        'wix.loyalty.referral.tracker.v1.ReferralEvents.QueryReferredFriendActions',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixLoyaltyReferralTrackerV1ReferralEventsUrl({
        protoPath: '/v1/referred-friend-actions/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'referredFriendActions.actionDate' },
              { path: 'referredFriendActions.signupDate' },
            ],
          },
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'referredFriendActions.coupon.couponSpecification.minimumSubtotal',
              },
              {
                path: 'referredFriendActions.coupon.couponSpecification.fixedAmountOptions.amount',
              },
              {
                path: 'referredFriendActions.coupon.couponSpecification.percentageOptions.percentage',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryReferredFriendActions;
}
